// "Events on the timeline" — the Visual designer's editor for a time-series
// visual's EVENTS (LAYOUT_DATA.visualLayout.events; docs/visual_events.md).
//
// A layer is one source of events with its own name, colour and look:
//   • Type them in        — kind "manual" (a small grid of dates + labels)
//   • From a column       — kind "column" (the field in the Events drop zone)
//   • From another query  — kind "query"  (SQL via the shared Query builder,
//                                           then which column is which)
//   • Let people add notes — kind "notes" (who may add)
// What the layers RESOLVE to (counts, errors) is read back from the live
// preview's payload, so the author sees exactly what a viewer will.
// The shape and its limits are the visual-engine's (events/model.ts), which
// is the twin of the engine's normaliser — never a second copy here.

import { useEffect, useMemo, useState, type CSSProperties } from 'react'
import { createPortal } from 'react-dom'
import { Database, Eye, EyeOff, Flag, MessageSquare, Pencil, Plus, Rows3, Table2, X } from 'lucide-react'
import { SqlBuilderButton } from '@twasta/page-runtime/designerKit'
import {
  EVENTS_CONFIG, newLayer, normDate,
  type EventItem, type EventKind, type EventLayer, type EventsBlock,
} from '@twasta/visual-engine'
import { fetchSqlColumns } from '@/api/visualData'
import type { VisualModel } from './types'

export interface EventsFieldChoice { key: string; label: string; hint?: string }

export interface EventsPanelProps {
  events: EventsBlock | undefined
  onChange: (next: EventsBlock) => void
  readOnly: boolean
  project: string
  model: VisualModel
  /** `project` or `conn:<id>` — where a query layer's SQL runs. */
  target: string
  /** The field in the Events drop zone, if any. */
  eventsField: { label: string } | null
  /** Fields the author may pick for a column layer. */
  fieldChoices: EventsFieldChoice[]
  onPickField: (key: string) => void
  onClearField: () => void
  /** The visual's own criteria (for a query layer's ?.placeholders). */
  promptNames: string[]
  /** What the preview resolved the layers to. */
  resolved: EventsBlock | null
  /** A date clicked on the preview (add an event there). */
  addRequest: { date: string; label: string; rect: DOMRect | null } | null
  onAddRequestDone: () => void
}

const KIND_TEXT: Record<EventKind, string> = {
  manual: 'Typed in',
  column: 'From a column of this data',
  query: 'From another table or query',
  notes: 'Notes people add',
}

const s: Record<string, CSSProperties> = {
  card: { borderTop: '1px solid var(--color-border)', marginTop: 14, paddingTop: 12 },
  head: { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 },
  title: { fontWeight: 600, fontSize: 15, color: 'var(--color-text)' },
  count: { fontSize: 13, color: 'var(--color-text-secondary)', background: 'var(--color-surface-2)', borderRadius: 10, padding: '0 8px' },
  sub: { fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 8 },
  row: { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 4px 6px 8px', borderRadius: 6 },
  rowName: { fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'var(--color-text)' },
  rowSub: { fontSize: 13, color: 'var(--color-text-secondary)' },
  ibtn: { width: 30, height: 30, borderRadius: 6, border: '1px solid transparent', background: 'transparent', color: 'var(--color-text-secondary)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0 },
  btn: { font: 'inherit', fontSize: 14, fontWeight: 500, borderRadius: 6, padding: '6px 12px', minHeight: 34, border: '1px solid var(--color-border-strong)', background: 'var(--color-secondary-btn, var(--color-surface-2))', color: 'var(--color-text)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' },
  primary: { font: 'inherit', fontSize: 14, fontWeight: 600, borderRadius: 6, padding: '6px 14px', minHeight: 34, border: '1px solid var(--color-accent)', background: 'var(--color-accent)', color: '#fff', cursor: 'pointer' },
  danger: { font: 'inherit', fontSize: 14, fontWeight: 500, borderRadius: 6, padding: '6px 12px', minHeight: 34, border: '1px solid var(--color-error)', background: 'transparent', color: 'var(--color-error)', cursor: 'pointer' },
  menu: { position: 'absolute', right: 0, top: 40, width: 320, background: 'var(--color-bg-secondary)', border: '1px solid var(--color-border)', borderRadius: 8, boxShadow: '0 8px 24px rgba(16,24,40,.16)', padding: 6, zIndex: 30 },
  menuItem: { font: 'inherit', display: 'flex', gap: 10, alignItems: 'flex-start', width: '100%', textAlign: 'left', border: 0, background: 'transparent', padding: '9px 10px', borderRadius: 6, cursor: 'pointer', color: 'var(--color-text)' },
  inp: { font: 'inherit', fontSize: 14, width: '100%', boxSizing: 'border-box', minHeight: 34, padding: '6px 9px', border: '1px solid var(--color-border-strong)', borderRadius: 6, background: 'var(--color-input-bg)', color: 'var(--color-text)' },
  lbl: { display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 4 },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(17,24,39,.45)', zIndex: 1500, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '56px 16px', overflow: 'auto' },
  dialog: { width: 780, maxWidth: '100%', background: 'var(--color-bg-secondary)', color: 'var(--color-text)', borderRadius: 12, boxShadow: '0 20px 48px rgba(16,24,40,.3)', overflow: 'hidden' },
  th: { background: 'var(--color-surface-2)', color: 'var(--color-text-secondary)', fontSize: 13, fontWeight: 600, textAlign: 'left', padding: '7px 6px', borderBottom: '1px solid var(--color-border)' },
  td: { padding: '3px 3px', borderBottom: '1px solid var(--color-border)', verticalAlign: 'middle' },
  hint: { fontSize: 13, color: 'var(--color-text-secondary)', background: 'var(--color-surface-2)', borderRadius: 6, padding: '8px 10px' },
}

function Seg<T extends string>({ value, options, onPick, disabled }: {
  value: T; options: Array<[T, string]>; onPick: (v: T) => void; disabled?: boolean
}) {
  return (
    <div role="radiogroup" style={{ display: 'inline-flex', border: '1px solid var(--color-border-strong)', borderRadius: 6, overflow: 'hidden' }}>
      {options.map(([v, label], i) => (
        <button key={v} type="button" role="radio" aria-checked={value === v} disabled={disabled}
          onClick={() => onPick(v)}
          style={{
            font: 'inherit', fontSize: 13, fontWeight: 500, border: 0, padding: '5px 10px', minHeight: 30, cursor: 'pointer',
            borderLeft: i ? '1px solid var(--color-border-strong)' : 0,
            background: value === v ? 'var(--color-accent-weak)' : 'var(--color-input-bg)',
            color: value === v ? 'var(--color-accent)' : 'var(--color-text-secondary)',
          }}>{label}</button>
      ))}
    </div>
  )
}

const blankItem = (): EventItem => ({ date: '', end: '', label: '', category: '', note: '' })

function layersOf(events: EventsBlock | undefined): EventLayer[] {
  return Array.isArray(events?.layers) ? events!.layers : []
}

export default function EventsPanel(p: EventsPanelProps) {
  const layers = layersOf(p.events)
  // The menu is wider than the zone column it opens from, so it lives in a
  // portal, fixed under its button — never clipped by the panel's scroller.
  const [menu, setMenu] = useState<DOMRect | null>(null)
  useEffect(() => {
    if (!menu) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenu(null) }
    const onDown = (e: MouseEvent) => {
      const t = e.target as HTMLElement | null
      if (!t?.closest('[data-tw="visual-events-menu"]') && !t?.closest('[data-tw="visual-events-add"]')) setMenu(null)
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('mousedown', onDown)
    return () => { document.removeEventListener('keydown', onKey); document.removeEventListener('mousedown', onDown) }
  }, [menu])
  const [editing, setEditing] = useState<{ id: string; isNew: boolean; draft: EventLayer } | null>(null)
  const resolvedById = useMemo(() => {
    const m = new Map<string, EventLayer>()
    for (const l of layersOf(p.resolved ?? undefined)) m.set(l.id, l)
    return m
  }, [p.resolved])

  const commit = (next: EventLayer[]) => p.onChange({ layers: next })
  const lim = EVENTS_CONFIG.limits
  const full = layers.length >= lim.layers_max
  const has = (k: EventKind) => layers.some((l) => l.kind === k)

  const startNew = (kind: EventKind) => {
    setMenu(null)
    const existing = layers.find((l) => l.kind === kind)
    if ((kind === 'column' || kind === 'notes') && existing) {
      setEditing({ id: existing.id, isNew: false, draft: structuredClone(existing) })
      return
    }
    if (full) return
    const names: Record<EventKind, string> = { manual: 'Events', column: 'Events', query: 'Events', notes: 'Notes' }
    const draft = newLayer(kind, layers, names[kind])
    if (kind === 'manual') draft.items = [blankItem()]
    setEditing({ id: draft.id, isNew: true, draft })
  }

  // A date clicked on the preview → straight into a typed-in layer.
  const [quick, setQuick] = useState<{ date: string; label: string; rect: DOMRect | null; into: string; until: string; text: string } | null>(null)
  useEffect(() => {
    if (!p.addRequest || p.readOnly) return
    const manual = layers.find((l) => l.kind === 'manual')
    setQuick({ ...p.addRequest, into: manual ? manual.id : '__new', until: '', text: '' })
    p.onAddRequestDone()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.addRequest])

  const confirmQuick = () => {
    if (!quick) return
    const label = quick.text.trim()
    if (!label) return
    const date = normDate(quick.date) || quick.date
    const item: EventItem = { date, end: quick.until && quick.until > date ? quick.until : '', label, category: '', note: '' }
    const next = layers.map((l) => (l.id === quick.into ? { ...l, items: [...(l.items || []), item] } : l))
    if (quick.into === '__new') {
      if (full) return
      const nl = newLayer('manual', layers, 'Events')
      nl.items = [item]
      next.push(nl)
    }
    commit(next)
    setQuick(null)
  }

  return (
    <div style={s.card} data-tw="visual-events-panel">
      <div style={s.head}>
        <Flag size={15} style={{ color: 'var(--color-accent)' }} aria-hidden />
        <div style={s.title}>Events on the timeline</div>
        <span style={s.count}>{layers.length}</span>
        <div style={{ flex: 1 }} />
        {!p.readOnly && (
          <div style={{ position: 'relative' }}>
            <button type="button" style={s.btn} aria-expanded={!!menu} disabled={full && has('column') && has('notes')}
              onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); setMenu((v) => (v ? null : r)) }}
              data-tw="visual-events-add">
              <Plus size={14} aria-hidden /> Add events
            </button>
            {menu && createPortal(
              <div role="menu" data-tw="visual-events-menu" style={{
                ...s.menu, position: 'fixed', zIndex: 1400,
                top: Math.min(menu.bottom + 4, window.innerHeight - 280),
                left: Math.max(12, Math.min(menu.right - 320, window.innerWidth - 332)),
                right: 'auto',
              }}>
                {([
                  ['manual', Table2, 'Type them in', 'A short list you already know'],
                  ['column', Rows3, 'From a column of this data', p.eventsField ? `Now: ${p.eventsField.label}` : 'For example a promotion name on each row'],
                  ['query', Database, 'From another table or query', 'For example a Promotions or Price change table'],
                  ['notes', MessageSquare, 'Let people add notes', 'Viewers mark dates themselves in the app'],
                ] as Array<[EventKind, typeof Table2, string, string]>).map(([kind, Icon, t, sub]) => (
                  <button key={kind} type="button" role="menuitem" style={s.menuItem}
                    disabled={full && !((kind === 'column' || kind === 'notes') && has(kind))}
                    onClick={() => startNew(kind)} data-tw={`visual-events-add:${kind}`}>
                    <Icon size={18} style={{ color: 'var(--color-accent)', marginTop: 2, flexShrink: 0 }} aria-hidden />
                    <span>
                      <span style={{ display: 'block', fontWeight: 600 }}>{t}</span>
                      <span style={{ display: 'block', fontSize: 13, color: 'var(--color-text-secondary)' }}>{sub}</span>
                    </span>
                  </button>
                ))}
              </div>,
              document.body,
            )}
          </div>
        )}
      </div>
      <div style={s.sub}>Mark promotions, holidays, price changes or outages on the chart. Click a date on the preview to add one there.</div>

      {layers.length === 0 && (
        <div style={s.hint}>No events yet.</div>
      )}
      {layers.map((l) => {
        const r = resolvedById.get(l.id)
        const count = l.kind === 'column'
          ? (p.eventsField ? `Field: ${p.eventsField.label}` : 'No field in the Events zone yet')
          : r
            ? `${(r.items || []).length} on this chart${r.outside ? ` · ${r.outside} outside` : ''}`
            : l.kind === 'manual' ? `${(l.items || []).filter((i) => i.label && i.date).length} typed` : ''
        return (
          <div key={l.id} style={{ ...s.row, opacity: l.hidden ? 0.55 : 1 }}>
            <span style={{ width: 12, height: 12, borderRadius: 3, background: l.color, flexShrink: 0 }} />
            <div style={{ flex: '1 1 auto', minWidth: 0 }}>
              <div style={s.rowName}>{l.name}</div>
              <div style={s.rowSub}>
                {KIND_TEXT[l.kind]}{count ? ` · ${count}` : ''}
              </div>
              {r?.error && <div role="alert" style={{ fontSize: 13, color: 'var(--color-warning)' }}>{r.error}</div>}
            </div>
            {!p.readOnly && (
              <>
                <button type="button" style={s.ibtn} aria-label={l.hidden ? `Show ${l.name} when the chart opens` : `Hide ${l.name} when the chart opens`}
                  title={l.hidden ? 'Hidden when the chart opens (viewers can show it)' : 'Shown when the chart opens'}
                  onClick={() => commit(layers.map((x) => (x.id === l.id ? { ...x, hidden: !x.hidden } : x)))}>
                  {l.hidden ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
                <button type="button" style={s.ibtn} aria-label={`Edit ${l.name}`}
                  onClick={() => setEditing({ id: l.id, isNew: false, draft: structuredClone(l) })}>
                  <Pencil size={16} />
                </button>
              </>
            )}
          </div>
        )
      })}
      {(p.resolved?.notices || []).map((n) => (
        <div key={n} style={{ ...s.hint, marginTop: 6 }}>{n}</div>
      ))}

      {editing && (
        <LayerDialog
          {...p}
          draft={editing.draft}
          isNew={editing.isNew}
          resolvedLayer={resolvedById.get(editing.id) ?? null}
          onDraft={(d) => setEditing({ ...editing, draft: d })}
          onCancel={() => setEditing(null)}
          onRemove={() => {
            if (editing.draft.kind === 'column') p.onClearField()
            commit(layers.filter((l) => l.id !== editing.id))
            setEditing(null)
          }}
          onDone={() => {
            const d = editing.draft
            if (d.kind === 'manual') d.items = (d.items || []).filter((i) => i.label.trim() || i.date)
            commit(editing.isNew ? [...layers, d] : layers.map((l) => (l.id === editing.id ? d : l)))
            setEditing(null)
          }}
        />
      )}

      {quick && createPortal(
        <div role="dialog" aria-label={`Add an event on ${quick.label}`} data-tw="visual-event-quick-add" style={{
          position: 'fixed', zIndex: 1600, width: 300, maxWidth: 'calc(100vw - 24px)',
          left: Math.max(12, Math.min((quick.rect?.left ?? window.innerWidth / 2) - 150, window.innerWidth - 312)),
          top: Math.max(12, Math.min((quick.rect?.top ?? 160) + 14, window.innerHeight - 280)),
          background: 'var(--color-bg-secondary)', color: 'var(--color-text)', border: '1px solid var(--color-border)',
          borderRadius: 10, boxShadow: '0 12px 32px rgba(16,24,40,.22)', padding: 14,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
            <div style={{ fontWeight: 600, fontSize: 15, flex: 1 }}>Add an event on {quick.label}</div>
            <button type="button" style={s.ibtn} aria-label="Close" onClick={() => setQuick(null)}><X size={16} /></button>
          </div>
          <label style={s.lbl} htmlFor="tw-ev-quick-label">What happened</label>
          <input id="tw-ev-quick-label" autoFocus style={{ ...s.inp, marginBottom: 10 }} placeholder="For example Price increase"
            value={quick.text} maxLength={lim.label_max}
            onChange={(e) => setQuick({ ...quick, text: e.target.value })}
            onKeyDown={(e) => { if (e.key === 'Enter') confirmQuick() }} />
          <div style={{ display: 'flex', gap: 10, marginBottom: 12 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <label style={s.lbl} htmlFor="tw-ev-quick-until">Until (optional)</label>
              <input id="tw-ev-quick-until" type="date" style={s.inp} value={quick.until}
                onChange={(e) => setQuick({ ...quick, until: e.target.value })} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <label style={s.lbl} htmlFor="tw-ev-quick-into">Add to</label>
              <select id="tw-ev-quick-into" style={s.inp} value={quick.into}
                onChange={(e) => setQuick({ ...quick, into: e.target.value })}>
                {layers.filter((l) => l.kind === 'manual').map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                {!full && <option value="__new">A new list</option>}
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button type="button" style={s.btn} onClick={() => setQuick(null)}>Cancel</button>
            <button type="button" style={s.primary} disabled={!quick.text.trim()} onClick={confirmQuick}>Add</button>
          </div>
        </div>,
        document.body,
      )}
    </div>
  )
}

// ── one layer's dialog ───────────────────────────────────────────────────

function LayerDialog(p: EventsPanelProps & {
  draft: EventLayer
  isNew: boolean
  resolvedLayer: EventLayer | null
  onDraft: (d: EventLayer) => void
  onCancel: () => void
  onRemove: () => void
  onDone: () => void
}) {
  const d = p.draft
  const set = (patch: Partial<EventLayer>) => p.onDraft({ ...d, ...patch })
  const lim = EVENTS_CONFIG.limits
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') p.onCancel() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [p])

  return createPortal(
    <div style={s.overlay} onMouseDown={(e) => { if (e.target === e.currentTarget) p.onCancel() }}>
      <div role="dialog" aria-label={p.isNew ? 'New events' : d.name} style={s.dialog} data-tw={`visual-events-dialog:${d.kind}`}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 18px', borderBottom: '1px solid var(--color-border)' }}>
          <span style={{ width: 12, height: 12, borderRadius: 3, background: d.color }} />
          <div style={{ fontWeight: 600, fontSize: 17 }}>{p.isNew ? 'New events' : d.name}</div>
          <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{KIND_TEXT[d.kind]}</span>
          <div style={{ flex: 1 }} />
          <button type="button" style={s.ibtn} aria-label="Close" onClick={p.onCancel}><X size={16} /></button>
        </div>

        <div style={{ padding: '16px 18px', maxHeight: '65vh', overflow: 'auto' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px 18px', alignItems: 'flex-end', marginBottom: 16 }}>
            <div style={{ flex: '1 1 200px', minWidth: 0 }}>
              <label style={s.lbl} htmlFor="tw-ev-name">Name on the chart</label>
              <input id="tw-ev-name" style={s.inp} value={d.name} maxLength={lim.name_max}
                onChange={(e) => set({ name: e.target.value })} />
            </div>
            <div>
              <span style={s.lbl}>Colour</span>
              <div style={{ display: 'flex', gap: 6 }}>
                {EVENTS_CONFIG.palette.map((c) => (
                  <button key={c} type="button" aria-label={`Colour ${c}`} aria-pressed={d.color === c}
                    onClick={() => set({ color: c })}
                    style={{ width: 24, height: 24, borderRadius: '50%', padding: 0, cursor: 'pointer', background: c,
                      border: `3px solid ${d.color === c ? 'var(--color-text)' : 'var(--color-bg-secondary)'}`,
                      boxShadow: '0 0 0 1px var(--color-border)' }} />
                ))}
              </div>
            </div>
            <div>
              <span style={s.lbl}>Show as</span>
              <Seg value={d.look} onPick={(v) => set({ look: v })}
                options={[['auto', 'Auto'], ['line', 'Line'], ['band', 'Band'], ['flag', 'Flag']]} />
            </div>
            <div>
              <span style={s.lbl}>Labels</span>
              <Seg value={d.labels} onPick={(v) => set({ labels: v })}
                options={[['hover', 'On hover'], ['always', 'Always']]} />
            </div>
          </div>
          <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: -8, marginBottom: 14 }}>
            Auto draws a dashed line for one day and shades a period that has an end date.
          </div>

          {d.kind === 'manual' && <ManualEditor draft={d} set={set} />}
          {d.kind === 'column' && (
            <ColumnEditor eventsField={p.eventsField} choices={p.fieldChoices} onPick={p.onPickField} />
          )}
          {d.kind === 'query' && (
            <QueryEditor {...p} draft={d} set={set} />
          )}
          {d.kind === 'notes' && (
            <div>
              <div style={{ fontSize: 14, color: 'var(--color-text-secondary)', marginBottom: 12 }}>
                People using the app add notes straight on the chart with <b>+ Note</b>. Notes are kept for each
                organization, so one organization never sees another's. People edit and delete their own;
                functional admins can tidy everyone's.
              </div>
              <span style={s.lbl}>Who can add notes</span>
              {([['viewers', 'Everyone who can see this chart', 'Anyone with access to the visual can add a note.'],
                ['admins', 'Functional admins only', 'Others can read the notes but not add them.']] as const).map(([v, t, h]) => (
                <label key={v} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '8px 10px', marginBottom: 4,
                  border: `1px solid ${d.who_can_add === v ? 'var(--color-accent)' : 'var(--color-border)'}`, borderRadius: 6,
                  background: d.who_can_add === v ? 'var(--color-accent-weak)' : 'transparent', cursor: 'pointer' }}>
                  <input type="radio" name="tw-ev-who" checked={d.who_can_add === v} onChange={() => set({ who_can_add: v })} style={{ marginTop: 3 }} />
                  <span><span style={{ display: 'block', fontWeight: 600 }}>{t}</span>
                    <span style={{ display: 'block', fontSize: 13, color: 'var(--color-text-secondary)' }}>{h}</span></span>
                </label>
              ))}
            </div>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 18px', borderTop: '1px solid var(--color-border)', background: 'var(--color-surface-2)' }}>
          {!p.isNew && <button type="button" style={s.danger} onClick={p.onRemove}>Remove these events</button>}
          <div style={{ flex: 1 }} />
          <button type="button" style={s.btn} onClick={p.onCancel}>Cancel</button>
          <button type="button" style={s.primary} onClick={p.onDone} data-tw="visual-events-done">
            {p.isNew ? 'Add to chart' : 'Done'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}

function ManualEditor({ draft, set }: { draft: EventLayer; set: (p: Partial<EventLayer>) => void }) {
  const items = draft.items || []
  const lim = EVENTS_CONFIG.limits
  const upd = (i: number, patch: Partial<EventItem>) => set({ items: items.map((it, k) => (k === i ? { ...it, ...patch } : it)) })
  return (
    <div>
      <div style={{ fontSize: 14, color: 'var(--color-text-secondary)', marginBottom: 8 }}>
        Add an end date to shade a period. Rows without a date or a label are not shown.
      </div>
      <div style={{ border: '1px solid var(--color-border)', borderRadius: 8, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead><tr>
            <th style={{ ...s.th, width: 140 }}>Date</th><th style={{ ...s.th, width: 140 }}>Until (optional)</th>
            <th style={s.th}>Label</th><th style={{ ...s.th, width: 120 }}>Category</th><th style={s.th}>Note</th>
            <th style={{ ...s.th, width: 34 }} />
          </tr></thead>
          <tbody>
            {items.map((it, i) => (
              <tr key={i}>
                <td style={s.td}><input type="date" aria-label="Date" style={s.inp} value={(it.date || '').slice(0, 10)} onChange={(e) => upd(i, { date: e.target.value })} /></td>
                <td style={s.td}><input type="date" aria-label="Until" style={s.inp} value={(it.end || '').slice(0, 10)} min={(it.date || '').slice(0, 10) || undefined} onChange={(e) => upd(i, { end: e.target.value })} /></td>
                <td style={s.td}><input aria-label="Label" style={s.inp} placeholder="What happened" maxLength={lim.label_max} value={it.label} onChange={(e) => upd(i, { label: e.target.value })} /></td>
                <td style={s.td}><input aria-label="Category" style={s.inp} maxLength={lim.label_max} value={it.category} onChange={(e) => upd(i, { category: e.target.value })} /></td>
                <td style={s.td}><input aria-label="Note" style={s.inp} maxLength={lim.text_max} value={it.note} onChange={(e) => upd(i, { note: e.target.value })} /></td>
                <td style={s.td}><button type="button" style={s.ibtn} aria-label="Remove row" onClick={() => set({ items: items.filter((_, k) => k !== i) })}><X size={15} /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button type="button" style={{ ...s.btn, marginTop: 8, background: 'transparent', border: 0, color: 'var(--color-accent)' }}
        disabled={items.length >= lim.items_max}
        onClick={() => set({ items: [...items, { date: '', end: '', label: '', category: '', note: '' }] })}>
        <Plus size={14} aria-hidden /> Add a row
      </button>
    </div>
  )
}

function ColumnEditor({ eventsField, choices, onPick }: {
  eventsField: { label: string } | null; choices: EventsFieldChoice[]; onPick: (key: string) => void
}) {
  return (
    <div>
      <div style={{ fontSize: 14, color: 'var(--color-text-secondary)', marginBottom: 10 }}>
        Pick the field whose value names the event. On every date where it has a value, the chart shows an event with
        that value as its label. This is the same as dropping the field into the <b>Events</b> zone. A chart has one
        Events field; several different values on one date show together as “2 events”.
      </div>
      <span style={s.lbl}>Field</span>
      {eventsField && (
        <div style={{ ...s.hint, marginBottom: 8 }}>Now: <b>{eventsField.label}</b></div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, maxHeight: 260, overflow: 'auto' }}>
        {choices.length === 0 && <div style={s.hint}>No text field is available — pick the data first.</div>}
        {choices.map((c) => {
          const on = eventsField?.label === c.label
          return (
            <label key={c.key} style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '7px 10px',
              border: `1px solid ${on ? 'var(--color-accent)' : 'var(--color-border)'}`, borderRadius: 6,
              background: on ? 'var(--color-accent-weak)' : 'transparent', cursor: 'pointer' }}>
              <input type="radio" name="tw-ev-field" checked={on} onChange={() => onPick(c.key)} />
              <span style={{ fontWeight: 600 }}>{c.label}</span>
              {c.hint && <span style={{ fontSize: 13, color: 'var(--color-text-secondary)' }}>{c.hint}</span>}
            </label>
          )
        })}
      </div>
    </div>
  )
}

function QueryEditor(p: EventsPanelProps & { draft: EventLayer; resolvedLayer: EventLayer | null; set: (patch: Partial<EventLayer>) => void }) {
  const d = p.draft
  const map = d.map || { date: '', end: '', label: '', category: '', note: '' }
  const [cols, setCols] = useState<string[]>([])
  const [colErr, setColErr] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  // The query's own columns, asked of the database (prompts bind NULL here).
  useEffect(() => {
    const sql = (d.sql || '').trim()
    if (!sql) { setCols([]); setColErr(null); return }
    let cancelled = false
    const t = setTimeout(async () => {
      setLoading(true)
      try {
        const res = await fetchSqlColumns({
          project: p.project,
          model: { ...p.model, DATA_SOURCE_MODE: 'sql', SOURCE_SQL: sql },
        })
        if (cancelled) return
        const names = (res.columns || []).map((c) => c.name)
        setCols(names)
        setColErr(null)
        // First time: guess which column is which.
        if (!map.date && !map.label && names.length) {
          const find = (re: RegExp) => names.find((n) => re.test(n)) || ''
          p.set({ map: {
            date: find(/date|_dt$|^dt|start|day|_on$/i) || names[0],
            end: find(/end|until|to_dt|finish/i),
            label: find(/title|name|label|event|desc|note/i) || names[1] || names[0],
            category: find(/kind|type|category|group/i),
            note: '',
          } })
        }
      } catch (e) {
        if (cancelled) return
        const any = e as { response?: { data?: { detail?: unknown } }; message?: string }
        const det = any?.response?.data?.detail
        setColErr(typeof det === 'string' ? det : (det && typeof det === 'object' && 'message' in det ? String((det as { message: unknown }).message) : any?.message || 'The query could not be read.'))
        setCols([])
      } finally {
        if (!cancelled) setLoading(false)
      }
    }, 700)
    return () => { cancelled = true; clearTimeout(t) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d.sql, p.project, p.target])

  const placeholders = useMemo(() => {
    const out = new Set<string>()
    for (const m of (d.sql || '').matchAll(/\?\.(@?)([A-Za-z_][A-Za-z0-9_]*)/g)) if (!m[1]) out.add(m[2])
    return [...out]
  }, [d.sql])
  const known = new Set(p.promptNames.map((n) => n.toUpperCase()))
  const unknown = placeholders.filter((n) => !known.has(n.toUpperCase()))
  const opts = ['', ...cols, ...Object.values(map).filter((v) => v && !cols.includes(v))]

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <div style={{ fontWeight: 600 }}>1 · Where the events come from</div>
        <div style={{ flex: 1 }} />
        <SqlBuilderButton value={d.sql || ''} onChange={(sql) => p.set({ sql })} project={p.project} target={p.target}
          title="Events query" layoutKey={`visual-events:${d.id}`} />
      </div>
      <textarea aria-label="Events query" spellCheck={false} rows={5}
        style={{ ...s.inp, fontFamily: 'var(--font-mono, monospace)', fontSize: 13, resize: 'vertical' }}
        placeholder={'SELECT start_date, end_date, title FROM promotion WHERE region = ?.region'}
        value={d.sql || ''} maxLength={EVENTS_CONFIG.limits.sql_max}
        onChange={(e) => p.set({ sql: e.target.value })} />
      <div style={{ fontSize: 13, margin: '4px 0 14px', color: unknown.length ? 'var(--color-warning)' : 'var(--color-text-secondary)' }}>
        {unknown.length
          ? `?.${unknown.join(', ?.')} ${unknown.length === 1 ? 'is' : 'are'} not one of this chart's criteria — the query will fail.`
          : placeholders.length
            ? `✓ Uses this chart's criteria (${placeholders.map((n) => '?.' + n).join(', ')}), so the events follow the same filter.`
            : "Use ?.<criterion> to follow the chart's own filters. Only events inside the chart's dates are shown."}
      </div>

      <div style={{ fontWeight: 600, marginBottom: 8 }}>2 · Which column is which</div>
      {colErr && <div role="alert" style={{ fontSize: 13, color: 'var(--color-error)', marginBottom: 8 }}>{colErr}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '10px 16px', marginBottom: 14 }}>
        {(([['date', 'Date *'], ['end', 'Until (for a period)'], ['label', 'Label *'], ['category', 'Category'], ['note', 'Note']]) as Array<[keyof typeof map, string]>).map(([f, label]) => (
          <div key={f}>
            <label style={s.lbl} htmlFor={`tw-ev-map-${f}`}>{label}</label>
            <select id={`tw-ev-map-${f}`} style={{ ...s.inp, fontFamily: 'var(--font-mono, monospace)', fontSize: 13 }}
              value={map[f] || ''} disabled={loading && !cols.length}
              onChange={(e) => p.set({ map: { ...map, [f]: e.target.value } })}>
              {opts.map((o) => <option key={o || '__none'} value={o}>{o || '— none —'}</option>)}
            </select>
          </div>
        ))}
      </div>

      <div style={{ fontWeight: 600, marginBottom: 6 }}>3 · Check</div>
      <div style={s.hint}>
        {p.resolvedLayer
          ? p.resolvedLayer.error
            ? <span style={{ color: 'var(--color-warning)' }}>{p.resolvedLayer.error}</span>
            : `✓ ${(p.resolvedLayer.items || []).length} events on the chart${p.resolvedLayer.outside ? `, ${p.resolvedLayer.outside} outside its dates` : ''} — from the preview.`
          : 'Press Done: the preview runs the query and the count shows here.'}
      </div>
    </div>
  )
}
